import { Doctor } from 'domain/entities/doctor-model'
import { Patient } from 'domain/entities/patient-model'
import { FormikProvider, useFormik } from 'formik'
import moment from 'moment'
import { useServices } from 'presentation/hooks/use-services'
import Button from 'presentation/shared/components/Button'
import {
  AmbulatoryScheduleDoctorInfo,
  AmbulatoryScheduleInfo,
  AmbulatorySchedulePatientInfo
} from 'presentation/hospital/components/AmbulatoryScheduleDetails'
import Container from 'presentation/hospital/components/Container'
import ActualPage from 'presentation/shared/components/ActualPage'
import Heading from 'presentation/shared/components/Heading'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import { SchedulesRow } from '../Home/Ambulatory'
import * as S from './styles'
import { cpfMask, phoneMask, landlinePhoneMask } from 'presentation/utils/masks'
import { conformToMask } from 'react-text-mask'
import {
  AmbulatoryScheduleDetailsEditFormikValues,
  ambulatoryScheduleDetailsValidationSchema
} from './types'
import { WithLoading } from 'presentation/shared/components/HOCs/WithLoading'
import TypedModal from 'presentation/shared/components/Modal'

export default WithLoading(function AmbulatoryScheduleDetails({
  setIsLoading
}) {
  const [patient, setPatient] = useState<Patient>()
  const [checkModal, setcheckModal] = useState<boolean>(false)
  const [doctor, setDoctor] = useState<Doctor>()
  const patientService = useServices().patient
  const doctorService = useServices().doctor
  const history = useHistory()
  const { state } = useLocation<{
    schedule: SchedulesRow
    allowEdit?: boolean
    showLinkPassword?: boolean
  }>()

  const formik = useFormik<AmbulatoryScheduleDetailsEditFormikValues>({
    initialValues: {
      isEditing: false
    } as AmbulatoryScheduleDetailsEditFormikValues,
    validationSchema: ambulatoryScheduleDetailsValidationSchema,
    onSubmit: async (values) => {
      try {
        setIsLoading(true)

        const { updatePatient } = values
        delete updatePatient.cpf
        await patientService.updatePatientById({
          ...updatePatient,
          phone: updatePatient.phone?.replace(/\D+/g, ''),
          landlinePhone: updatePatient.landlinePhone?.replace(/\D+/g, ''),
          birthday: moment(updatePatient.birthday, 'DD/MM/YYYY').format(
            'YYYY-MM-DD'
          )
        })
      } catch (error: any) {
        toast.error(error.message)
      } finally {
        setcheckModal(false)
        setIsLoading(false)
        toogleEdit()
      }
    }
  })

  const { isEditing } = formik.values

  const setData = (
    data: Partial<AmbulatoryScheduleDetailsEditFormikValues>
  ) => {
    formik.setValues({ ...formik.values, ...data }, true)
  }

  const getInitialPatient = () => {
    if (!patient) return
    return {
      ...patient,
      patient_id: state.schedule.patient_id!,
      birthday: moment(patient?.birthday).format('DD/MM/YYYY') || '',
      cpf: conformToMask(patient?.cpf, cpfMask)?.conformedValue || '',
      phone: conformToMask(patient?.phone || '', phoneMask)?.conformedValue,
      landlinePhone: conformToMask(
        patient?.landlinePhone || '',
        landlinePhoneMask
      )?.conformedValue,
      name: patient?.name || '',
      gender: patient?.gender || '',
      email: patient?.email || ''
    }
  }

  useEffect(() => {
    const updatePatient = getInitialPatient()
    setData({
      updatePatient
    })
  }, [patient])

  const handleSubmit = () => {
    formik.handleSubmit()
  }

  const toogleEdit = async () => {
    formik.setFieldValue('isEditing', !isEditing)
  }

  const getPatient = async (patient_id: number) => {
    try {
      const findPatient = await patientService.loadPatientInfo(
        [
          'birthday',
          'gender',
          'phone',
          'landlinePhone',
          'email',
          'name',
          'cpf'
        ],
        patient_id
      )
      setPatient(findPatient)
    } catch (error) {
      toast.error('Erro aos buscar informações do paciente')
    }
  }

  const getDoctor = async (doctor_id: number) => {
    try {
      const findDoctor = await doctorService.loadDoctorInfo(doctor_id, [
        'name',
        'crm',
        'crmUf',
        'specialty'
      ])
      setDoctor(findDoctor)
    } catch (error) {
      toast.error('Erro aos buscar informações do médico')
    }
  }

  useEffect(() => {
    if (state?.schedule?.patient_id) {
      getPatient(state?.schedule?.patient_id)
    }
    if (state?.schedule?.doctor_id) {
      getDoctor(state?.schedule?.doctor_id)
    }
  }, [state?.schedule?.patient_id, state?.schedule?.doctor_id])

  return (
    <FormikProvider value={formik}>
      <Container>
        <Heading size="xlarge" color="gray">
          {patient?.name || 'Agendamento'}
        </Heading>
        <S.HeaderButtonsWrapper>
          <ActualPage text="Voltar" onClick={() => history.goBack()} />
          {state.showLinkPassword && (
            <Button type="button" onClick={() => null}>
              Vincular Senha
            </Button>
          )}
        </S.HeaderButtonsWrapper>
        <S.ContainerForm>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <div>
              <AmbulatorySchedulePatientInfo allowEdit={state.allowEdit} />
            </div>
            <div>
              <AmbulatoryScheduleDoctorInfo doctor={doctor} />
            </div>
          </div>
          <AmbulatoryScheduleInfo schedule={state?.schedule} />
          {state.allowEdit && (
            <S.ButtonWrapper>
              {isEditing && (
                <Button type="button" variant="outlined" onClick={toogleEdit}>
                  Cancelar
                </Button>
              )}
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={!isEditing}
              >
                Salvar
              </Button>
            </S.ButtonWrapper>
          )}
        </S.ContainerForm>
        <TypedModal
          type="check"
          show={checkModal}
          actionLabel="Fechar"
          action={() => setcheckModal(false)}
          close={() => setcheckModal(false)}
          title={'Os dados foram salvos'}
          style={{ width: '400px' }}
          preventAutomateClose
        />
      </Container>
    </FormikProvider>
  )
})
